import { useState, useRef, memo, useEffect, useMemo } from "react";
import LOCATIONS from "./countries+states+cities.json";
import { SearchDropdown } from "./SearchDropdown";

const MAX_RESULTS = 50;

export const SearchLocation = memo((props) => {
  const {
    defaultValue,
    label,
    placeholder,
    errorMsg,
    helperText,
    types = ["country"],
    width = "w-[358px] lg:w-[384px] max-[400px]:w-[100%]",
    onSelectLocation,
    searchBy = "",
    disabled,
    isShadow = false,
  } = props || {};

  const stateListRaw = useMemo(() => {
    return LOCATIONS.map((loc) => `${loc.name} - ${loc.state_code}`);
  }, []);

  const allCities = useMemo(() => {
    const cities = LOCATIONS.flatMap((loc) =>
      loc.cities.map((city) => city.name)
    );
    return [...new Set(cities)];
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStateCode, setSelectedStateCode] = useState("");
  const [filteredCities, setFilteredCities] = useState(allCities.slice(0, MAX_RESULTS));
  const [filteredStates, setFilteredStates] = useState(stateListRaw);
  // Track the last confirmed selection so blur can revert invalid free-typed text.
  // Use a ref (in addition to state) so handleBlur always reads the latest value
  // synchronously without waiting for a React re-render.
  const [confirmedValue, setConfirmedValue] = useState("");
  const confirmedValueRef = useRef("");

  const citiesBySelectedState = useMemo(() => {
    const selectedState = LOCATIONS.find(
      (loc) => `${loc.name} - ${loc.state_code}` === searchBy
    );
    return selectedState?.cities?.map((city) => city.name) || [];
  }, [searchBy]);

  useEffect(() => {
    if (searchBy) {
      setFilteredCities(citiesBySelectedState);
    }
  }, [searchBy, citiesBySelectedState]);

  useEffect(() => {
    if (types.includes("(cities)")) {
      setSearchTerm(defaultValue);
      setConfirmedValue(defaultValue || "");
      confirmedValueRef.current = defaultValue || "";
    } else {
      // For state: defaultValue is the full "Alaska - AK" string.
      // Display only the code ("AK") by setting selectedStateCode.
      // Do NOT set searchTerm here — SearchDropdown uses selectedStateCode for display.
      setFilteredStates(stateListRaw);
      const code = defaultValue ? defaultValue.split(" - ")[1] || "" : "";
      setSelectedStateCode(code);
      const confirmed = defaultValue || "";
      setConfirmedValue(confirmed);
      confirmedValueRef.current = confirmed;
    }
  }, [defaultValue, types, stateListRaw]);

  const handleSearchChange = (value, type) => {
    // Only update the visual search term — do NOT call onSelectLocation here.
    // onSelectLocation is only called when the user picks from the dropdown.
    setSearchTerm(value);

    // Filter the dropdown list as the user types
    const q = value.toLowerCase();
    if (type === "City") {
      const sourceCities = searchBy ? citiesBySelectedState : allCities;
      const matchedCities = value
        ? sourceCities.filter((city) => city.toLowerCase().includes(q)).slice(0, MAX_RESULTS)
        : sourceCities.slice(0, MAX_RESULTS);
      setFilteredCities(matchedCities);
    } else {
      // Matches "Tennessee - TN" when typing "T", "TN", "Ten", "Cali", "CA", etc.
      const matchedStates = value
        ? stateListRaw.filter((state) => state.toLowerCase().includes(q))
        : stateListRaw;
      setFilteredStates(matchedStates);
    }
  };

  const handleLocationSelect = (value) => {
    // User picked from dropdown — this is the only place we notify the parent
    onSelectLocation(value);

    if (!types.includes("(cities)")) {
      const code = value ? value.split(" - ")[1] : "";
      setSelectedStateCode(code);
      // Update both state and ref immediately so blur reads the correct value
      setConfirmedValue(value || "");
      confirmedValueRef.current = value || "";
    } else {
      setSearchTerm(value);
      setConfirmedValue(value || "");
      confirmedValueRef.current = value || "";
    }
  };

  // On blur: revert display to last confirmed valid selection.
  // typedValue is what the user currently has in the input box.
  // If it doesn't match the confirmed selection, clear the parent so validation blocks.
  const handleBlur = (typedValue) => {
    const confirmed = confirmedValueRef.current;

    if (isCityMode) {
      // City allows free text — accept whatever the user typed
      setSearchTerm(typedValue);
      setConfirmedValue(typedValue);
      confirmedValueRef.current = typedValue;
      onSelectLocation(typedValue);
    } else {
      // For state the input shows the code e.g. "AK", confirmed is "Alaska - AK"
      const confirmedCode = confirmed ? confirmed.split(" - ")[1] || "" : "";
      const userMatchesConfirmed = typedValue === confirmedCode;
      setSelectedStateCode(confirmedCode);
      // If the user typed something different from the confirmed code, it means
      // they edited the field without picking from the dropdown — clear the parent
      if (!userMatchesConfirmed) {
        onSelectLocation("");
      }
    }
  };

  const isCityMode = types.includes("(cities)");

  return (
    <div className={`relative ${width}`}>
      {label && (
        <div className="mb-0.5">
          <span className="text-gray-700 font-medium text-sm">{label}</span>
        </div>
      )}
      {helperText && (
        <div className="mb-1.5">
          <span className="text-gray-600 text-xs italic">
            {helperText}
          </span>
        </div>
      )}
      <div className="flex items-center w-full">
        <SearchDropdown
          isShadow={isShadow}
          showCancel={true}
          dropdownOptions={isCityMode ? filteredCities : filteredStates}
          onChangeSearchText={handleSearchChange}
          onSelectOption={handleLocationSelect}
          onBlur={handleBlur}
          width={width}
          placeholder={placeholder}
          defaultValue={isCityMode ? searchTerm : selectedStateCode}
          disabled={disabled}
          decoratedValue={
            !isCityMode ? (value) => value?.split(" - ")[1] || "" : undefined
          }
        />
      </div>
      {errorMsg && (
        <span className="text-red font-medium text-sm mt-1 pt-2">
          {errorMsg}
        </span>
      )}
    </div>
  );
});
